import * as THREE from 'three';
import { blankFace, bronze, canDraw, type GlyphFace } from './hud-materials.js';
import { boltGeometry, cogGeometry, copiesGeometry, iron, unpickable } from './hud-parts.js';
import { BUTTON, ButtonKit } from './octagon-button.js';

export { unpickable };

/** One entry of the radial menu: an ability, or a group whose children open in the next ring out. */
export interface RadialNode {
  id: string; icon: string; tint: number; autocast?: boolean | null; children?: RadialNode[];
  /** Greyed out: the rules would refuse it now. Its glyph turns cold grey in a dull iron bezel, and it neither lifts nor glows. */
  disabled?: boolean;
  /** Waiting for its target, or a stance being held: the rim glows. */
  armed?: boolean;
}

/** Screen sizes in CSS pixels; the menu never scales with the camera's zoom. */
const FIRST_RING = 84, RING_STEP = 58, SOCKET = BUTTON;
/** Seconds for a socket to swing out to its place, and the delay between neighbours. */
const APPEAR = .22, STAGGER = .035, FOLD = .16, PULSE = .5;
/** How far in front of the band the buttons stand, so they never cut into it. */
const BUTTON_Z = 16;
/** A greyed entry's glyph and enamel: cold iron grey instead of gilt on warm black. */
const GREY_INK = 0xa4abb1, GREY_FIELD = 0x15191c;

interface Socket {
  node: RadialNode; depth: number; index: number; group: THREE.Group; glints: THREE.Sprite[];
  angle: number; lift: number; flash: number; face?: GlyphFace; material: THREE.MeshStandardMaterial; glow: THREE.Sprite | null;
  /** The bezel and its rivets, which turn to dull iron while the entry is greyed, and its grey glyph. */
  trim: THREE.Mesh[]; greyFace?: GlyphFace; greyed: boolean;
  /** Where it swings out from (its group's socket, or the centre), where it rests, and how far along it is (0–1). */
  from: THREE.Vector2; to: THREE.Vector2; appear: number;
}
/** The menu's shape, so a state change (charges, autocast) updates in place and only a new set of entries rebuilds. */
const shape = (nodes: RadialNode[]): string => nodes.map(node => node.id + (node.children ? `(${shape(node.children)})` : '')).join(',');
/** Which shared glyph face an entry shows. */
const faceKey = (node: RadialNode, greyed: boolean) => `${node.icon}|${greyed ? 'grey' : node.tint}|${node.children ? 'cut' : 'raised'}`;
const easeOutBack = (t: number) => { const c = 1.6; return 1 + (c + 1) * (t - 1) ** 3 + c * (t - 1) ** 2; };

/**
 * A radial menu of lit bronze sockets drawn over the world around its target, at a fixed size on screen. The first ring is a
 * cast bronze band the sockets are set into, casting a soft shadow on the world, so the menu reads as one object while its middle
 * stays open on the target. A group opens its children as an arc in the next ring out, joined to it by a bronze stem; only one
 * group per ring is open at a time. Sockets swing out when they appear and fold back to the centre when the menu closes; a chosen
 * entry flashes and sends out a gilt pulse. A socket whose autocast is on has two gilt glints running round its rim.
 */
export class RadialMenu extends THREE.Group {
  private sockets = new Map<string, Socket>();
  private open: string[] = [];
  private arcs: THREE.Object3D[] = [];
  private metal = bronze(31, 0x5a4428);
  private dark = new THREE.MeshStandardMaterial({ color: 0x120c07, metalness: .3, roughness: .8 });
  // Octagonal buttons: an eight-sided enamel face in an eight-sided gilt bezel, on an iron cup, with a rivet at alternate corners.
  // Their geometry, gilt bezel, iron cup and drawn glyph faces are the HUD's button kit, shared with its rails.
  private kit: ButtonKit;
  private ownsKit: boolean;
  private hardware = iron();
  /** A cog behind the band that turns while the menu is open. */
  private cog: THREE.Mesh;
  /** A greyed entry's bezel: cold, blue-grey iron with no gilt and no glow, so it never reads as bronze under the menu's warm lamp. */
  private dullBezel = new THREE.MeshStandardMaterial({ color: 0x7c848b, metalness: .35, roughness: .55 });
  private band = new THREE.Group();
  private shadow: THREE.Mesh;
  private pulses: { mesh: THREE.Mesh; age: number }[] = [];
  /** The confirming ring's shape, and rings that have run their course, kept to be sent out again rather than made anew. */
  private pulseGeometry = new THREE.TorusGeometry(SOCKET, 1.6, 8, 48);
  private spareRings: THREE.Mesh[] = [];
  /** Every socket's group, for picking without building a list on each pointer move. */
  private pickable: THREE.Object3D[] = [];
  /** The two glints that run round an autocast socket's rim; every socket's pair is the same, so they share these. */
  private glintMaterials: THREE.SpriteMaterial[] = [];
  private shadeMaterial: THREE.SpriteMaterial | null = null;
  private shapeKey = '';
  private hovered: string | null = null;
  private time = 0;
  /** 1 while open, easing to 0 as the menu folds away. */
  private presence = 0;
  private folding = false;
  /**
   * Glyph faces by icon, colours and relief, made once (by the button kit) and shared by every socket and rail button that shows them.
   * Drawing one is a few milliseconds of pixel work and two texture uploads, so making them as a group opened was what hitched the frame.
   */
  private faces = new Map<string, Promise<GlyphFace | null>>();
  /** Faces asked for or queued so far, so the menu's refresh with every snapshot walks its entries without allocating. */
  private requested = new Set<string>();
  /** Entries whose faces are still to be drawn ahead of need, one per idle moment. */
  private warming: { node: RadialNode; greyed: boolean }[] = [];
  private warmingNow = false;
  private disposed = false;

  /** `kit` shares button parts and glyph faces with the rest of a layer; without it the menu makes and disposes its own. */
  constructor(private roots: RadialNode[], private glow: THREE.Texture | null, kit?: ButtonKit) {
    super();
    this.kit = kit ?? new ButtonKit(glow); this.ownsKit = !kit;
    // The band: an outer and an inner bronze rim with a dark channel between them, and studs between the sockets.
    const outer = new THREE.Mesh(new THREE.TorusGeometry(FIRST_RING + 7, 3.2, 12, 160), this.metal);
    const inner = new THREE.Mesh(new THREE.TorusGeometry(FIRST_RING - 7, 2.6, 12, 160), this.metal);
    const channel = new THREE.Mesh(new THREE.RingGeometry(FIRST_RING - 7, FIRST_RING + 7, 160), this.dark);
    outer.position.z = inner.position.z = -2; channel.position.z = -4;
    this.band.add(outer, inner, channel);
    // Hex bolts round the band, between where the sockets sit.
    this.band.add(new THREE.Mesh(copiesGeometry(boltGeometry(2.6), Array.from({ length: 12 }, (_, k) => {
      const a = (k + .5) / 12 * Math.PI * 2;
      return new THREE.Vector3(Math.sin(a) * FIRST_RING, Math.cos(a) * FIRST_RING, 1.5);
    })), this.hardware));
    this.cog = new THREE.Mesh(cogGeometry(FIRST_RING + 12, 30, 7, 5, FIRST_RING - 12), this.hardware); this.cog.position.z = -14;
    this.band.add(this.cog);
    // Its shadow on the world below and to the right, soft-edged, drawn in the shader so it needs no texture.
    this.shadow = new THREE.Mesh(new THREE.RingGeometry(FIRST_RING - 26, FIRST_RING + 30, 128, 1), new THREE.ShaderMaterial({
      transparent: true, depthWrite: false, toneMapped: false, uniforms: { fade: { value: 0 } },
      vertexShader: 'varying vec2 vPos; void main() { vPos = position.xy; gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); }',
      fragmentShader: `uniform float fade; varying vec2 vPos;
        void main() { float r = length(vPos); float d = abs(r - ${FIRST_RING.toFixed(1)}) / 26.0; gl_FragColor = vec4(0.0, 0.0, 0.0, fade * 0.42 * (1.0 - smoothstep(0.15, 1.0, d))); }`,
    }));
    this.shadow.position.set(5, -7, -40); this.shadow.renderOrder = -1;
    this.add(this.shadow, this.band);
    this.layout(); this.prewarm(roots);
  }
  /** The menu's reach from its centre, in its own units (CSS pixels before the interface size). */
  get radius() { return FIRST_RING + this.open.length * RING_STEP + SOCKET * 1.6; }
  /** True once a fold has finished; the owner hides the menu then. */
  get folded() { return this.folding && this.presence <= .001; }

  /** Brings the menu up from its centre, or keeps it up. */
  unfold() {
    if (!this.folding && this.presence > 0) return;
    this.restart();
  }
  /** Plays the opening again from the centre, for a menu moved to a new target. */
  restart() {
    this.folding = false; this.presence = 0;
    for (const socket of this.sockets.values()) { socket.from.set(0, 0); socket.appear = -socket.index * STAGGER / APPEAR; }
  }
  /** Folds every socket back into the centre; `folded` turns true when done. */
  fold() { this.folding = true; }

  /** Replaces the entries: their states update in place, and the rings rebuild only when the entries themselves changed. */
  setNodes(roots: RadialNode[]) {
    const key = shape(roots), rebuild = key !== this.shapeKey;
    this.shapeKey = key; this.roots = roots; this.prewarm(roots);
    const walk = (nodes: RadialNode[]) => { for (const node of nodes) { const socket = this.sockets.get(node.id); if (socket) socket.node = node; if (node.children) walk(node.children); } };
    walk(roots);
    if (rebuild) {
      // Keep the open path as far as it still exists.
      const ids = new Set<string>(); const all = (nodes: RadialNode[]) => { for (const node of nodes) { ids.add(node.id); if (node.children) all(node.children); } }; all(roots);
      const keep = this.open.findIndex(id => !ids.has(id)); if (keep >= 0) this.open = this.open.slice(0, keep);
      for (const socket of this.sockets.values()) { socket.group.removeFromParent(); }
      this.sockets.clear(); this.layout();
    }
  }
  /** Closes every open group. */
  collapse() { if (this.open.length) { this.open = []; this.layout(); } }
  node(id: string) { return this.sockets.get(id)?.node ?? null; }
  /** Opens or closes a group; any other group open at its depth or deeper closes. Returns whether the node was a group. */
  choose(id: string): boolean {
    const socket = this.sockets.get(id);
    if (!socket?.node.children) return false;
    socket.flash = 1;
    const wasOpen = this.open[socket.depth] === id;
    this.open = this.open.slice(0, socket.depth);
    if (!wasOpen) this.open[socket.depth] = id;
    this.layout();
    return true;
  }
  /** Marks a chosen entry: the socket flashes and a gilt ring pulses out from it. */
  confirm(id: string) {
    const socket = this.sockets.get(id);
    if (!socket) return;
    socket.flash = 1;
    const mesh = this.spareRings.pop() ?? new THREE.Mesh(this.pulseGeometry, new THREE.MeshBasicMaterial({ color: 0xffe2a8, transparent: true, depthWrite: false, blending: THREE.AdditiveBlending, toneMapped: false }));
    mesh.scale.setScalar(1); (mesh.material as THREE.MeshBasicMaterial).opacity = 1;
    mesh.position.set(socket.group.position.x, socket.group.position.y, BUTTON_Z + 14); this.add(mesh); this.pulses.push({ mesh, age: 0 });
  }
  hover(id: string | null) { this.hovered = id; }
  toggleAutocast(id: string) {
    const node = this.sockets.get(id)?.node;
    if (node && node.autocast !== null && node.autocast !== undefined) node.autocast = !node.autocast;
  }
  autocasts(): string[] {
    const on: string[] = [], walk = (nodes: RadialNode[]) => { for (const node of nodes) { if (node.autocast) on.push(node.id); if (node.children) walk(node.children); } };
    walk(this.roots); return on;
  }
  pick(raycaster: THREE.Raycaster): string | null {
    if (this.folding) return null;
    for (const hit of raycaster.intersectObjects(this.pickable, true)) {
      for (let object: THREE.Object3D | null = hit.object; object; object = object.parent) if (object.userData.socket) return object.userData.socket as string;
    }
    return null;
  }

  /** Builds the sockets for the current open path; sockets that stay keep their meshes and state. */
  private layout() {
    const wanted = new Map<string, { node: RadialNode; depth: number; angle: number; index: number; parent: string | null }>();
    const place = (nodes: RadialNode[], depth: number, centre: number, parent: string | null) => {
      const radius = FIRST_RING + depth * RING_STEP, step = depth === 0 ? Math.PI * 2 / nodes.length : (SOCKET * 2.5) / radius;
      nodes.forEach((node, index) => {
        const angle = depth === 0 ? centre + index * step : centre + (index - (nodes.length - 1) / 2) * step;
        wanted.set(node.id, { node, depth, angle, index, parent });
        if (node.children && this.open[depth] === node.id) place(node.children, depth + 1, angle, node.id);
      });
    };
    // The first socket sits at the top, and the ring runs clockwise like a clock face.
    place(this.roots, 0, 0, null);
    for (const [id, socket] of this.sockets) if (!wanted.has(id)) { socket.group.removeFromParent(); this.sockets.delete(id); }
    for (const [id, { node, depth, angle, index, parent }] of wanted) {
      const fresh = !this.sockets.has(id), socket = this.sockets.get(id) ?? this.build(node, depth);
      socket.angle = angle; socket.depth = depth; socket.index = index;
      const radius = FIRST_RING + depth * RING_STEP;
      socket.to.set(Math.sin(angle) * radius, Math.cos(angle) * radius);
      // A new socket swings out from its group's socket (or the centre), its neighbours a beat after it.
      if (fresh) { const from = parent ? this.sockets.get(parent)?.to : undefined; socket.from.copy(from ?? new THREE.Vector2()); socket.appear = -index * STAGGER / APPEAR; }
    }
    // A bronze arc behind each open group's children, and a stem from the group out to it.
    for (const arc of this.arcs) { arc.removeFromParent(); arc.traverse(object => { if (object instanceof THREE.Mesh) object.geometry.dispose(); }); }
    this.arcs = [];
    this.open.forEach((id, depth) => {
      const group = this.sockets.get(id), children = group?.node.children;
      if (!group || !children?.length) return;
      const angles = children.map(child => this.sockets.get(child.id)!.angle), from = Math.min(...angles), to = Math.max(...angles), radius = FIRST_RING + (depth + 1) * RING_STEP;
      const holder = new THREE.Group();
      const arc = new THREE.Mesh(new THREE.TorusGeometry(radius, 3, 10, 64, Math.max(.01, to - from)), this.metal);
      // Torus arcs start on +x and run anticlockwise; ours are measured clockwise from the top.
      arc.rotation.z = Math.PI / 2 - to; arc.position.z = -8;
      const inner = FIRST_RING + depth * RING_STEP + SOCKET, length = radius - inner;
      const stem = new THREE.Mesh(new THREE.CylinderGeometry(2.2, 2.2, length, 10), this.metal);
      stem.position.set(Math.sin(group.angle) * (inner + length / 2), Math.cos(group.angle) * (inner + length / 2), -8); stem.rotation.z = -group.angle;
      holder.add(arc, stem); this.add(holder); this.arcs.push(holder);
    });
    this.pickable.length = 0;
    for (const socket of this.sockets.values()) this.pickable.push(socket.group);
  }

  private build(node: RadialNode, depth: number): Socket {
    const group = new THREE.Group(); group.userData.socket = node.id;
    // Each button stands well in front of the band (its back clears the band's top), with a small shadow cast onto it.
    const { kit } = this, back = new THREE.Mesh(kit.back, kit.iron); back.position.z = -4;
    const rim = new THREE.Mesh(kit.rim, kit.bezel); rim.position.z = .5;
    // The kit's rivets sit where a socket's do (a socket is a button's size).
    const rivets = new THREE.Mesh(kit.rivets, kit.bezel), trim: THREE.Mesh[] = [rim, rivets];
    group.add(rivets);
    // Enamel, dark and a little glossy, so the raised gilt glyph reads against it; the glyph's own light comes through `emissive`.
    // It wears blank maps until its glyph is drawn, so the glyph arriving swaps textures and links no shader.
    const blank = blankFace();
    const material = new THREE.MeshStandardMaterial({ color: 0x1c140c, metalness: .2, roughness: .38, emissive: 0x000000, emissiveIntensity: .55, map: blank.map, normalMap: blank.normalMap, emissiveMap: blank.map });
    const face = new THREE.Mesh(kit.face, material); face.position.z = 1;
    group.add(back, rim, face);
    if (this.glow) {
      const shade = new THREE.Sprite(this.shadeMaterial ??= new THREE.SpriteMaterial({ map: this.glow, color: 0x000000, transparent: true, depthWrite: false, opacity: .55 }));
      unpickable(shade); shade.scale.setScalar(SOCKET * 3.2); shade.position.set(3, -5, -10); group.add(shade);
    }
    const glow = this.glow ? new THREE.Sprite(new THREE.SpriteMaterial({ map: this.glow, color: 0xffc46b, blending: THREE.AdditiveBlending, depthWrite: false, transparent: true, opacity: 0 })) : null;
    if (glow) { unpickable(glow); glow.scale.setScalar(SOCKET * 4); glow.position.z = -6; group.add(glow); }
    const glints = node.autocast === null || node.autocast === undefined || !this.glow ? [] : [0, 1].map(k => {
      const sprite = new THREE.Sprite(this.glintMaterials[k] ??= new THREE.SpriteMaterial({ map: this.glow, color: 0xffe2a8, blending: THREE.AdditiveBlending, depthWrite: false, transparent: true }));
      unpickable(sprite); sprite.scale.setScalar(16); group.add(sprite); return sprite;
    });
    this.add(group);
    const socket: Socket = { node, depth, index: 0, group, glints, angle: 0, lift: 0, flash: 0, material, glow, trim, greyed: false, from: new THREE.Vector2(), to: new THREE.Vector2(), appear: 0 };
    this.sockets.set(node.id, socket);
    // Groups carry their glyph cut into the face, abilities raised from it, so a folder reads differently from a power.
    void this.faceFor(node, false).then(texture => {
      if (!texture || this.sockets.get(node.id) !== socket) return;
      socket.face = texture; this.paint(socket);
    });
    this.paint(socket);
    return socket;
  }
  /** Dresses a socket as available (gilt glyph, gilt bezel) or greyed (grey glyph, iron bezel). */
  private paint(socket: Socket) {
    const greyed = socket.greyed = !!socket.node.disabled;
    for (const mesh of socket.trim) mesh.material = greyed ? this.dullBezel : this.kit.bezel;
    if (greyed && !socket.greyFace) {
      const node = socket.node;
      void this.faceFor(node, true).then(texture => {
        if (!texture || this.sockets.get(node.id) !== socket || socket.greyFace) return;
        socket.greyFace = texture; this.paint(socket);
      });
    }
    const face = greyed ? socket.greyFace ?? socket.face : socket.face;
    if (!face) return;
    const { material } = socket;
    material.map = material.emissiveMap = face.map; material.normalMap = face.normalMap; material.emissive.setHex(greyed ? 0x252b31 : 0x6a4a1c);
  }

  /** The shared face for an entry, drawn on first need. Groups carry their glyph cut into the face, abilities raised from it. */
  private faceFor(node: RadialNode, greyed: boolean): Promise<GlyphFace | null> {
    const key = faceKey(node, greyed);
    let face = this.faces.get(key);
    if (!face) {
      face = this.kit.glyph(node.icon, greyed ? GREY_FIELD : 0x1c140c, greyed ? GREY_INK : node.tint, !node.children);
      this.faces.set(key, face); this.requested.add(key);
    }
    return face;
  }
  /** Queues the faces of every entry, the closed groups' children included, to be drawn in idle moments before they are opened. */
  private prewarm(roots: RadialNode[]) {
    if (!canDraw()) return;
    // The menu is refreshed with every snapshot: queue only faces neither drawn nor already waiting.
    this.queue(roots);
    if (!this.warmingNow && this.warming.length) this.warmNext();
  }
  private queue(nodes: RadialNode[]) {
    for (const node of nodes) {
      this.want(node, false); if (node.disabled) this.want(node, true);
      if (node.children) this.queue(node.children);
    }
  }
  private want(node: RadialNode, greyed: boolean) {
    const key = faceKey(node, greyed);
    if (this.requested.has(key)) return;
    this.requested.add(key); this.warming.push({ node, greyed });
  }
  private warmNext = () => {
    let next = this.warming.shift();
    while (next && this.faces.has(faceKey(next.node, next.greyed))) next = this.warming.shift();
    if (!next || this.disposed) { this.warming = []; this.warmingNow = false; return; }
    this.warmingNow = true;
    const later = () => typeof requestIdleCallback === 'function' ? requestIdleCallback(this.warmNext, { timeout: 500 }) : setTimeout(this.warmNext, 16);
    void this.faceFor(next.node, next.greyed).finally(later);
  };

  update(dt: number) {
    this.time += dt;
    this.presence = this.folding ? Math.max(0, this.presence - dt / FOLD) : Math.min(1, this.presence + dt / APPEAR);
    const presence = this.folding ? this.presence ** 2 : easeOutBack(this.presence);
    this.band.scale.setScalar(.7 + presence * .3);
    // The cog turns while the menu is open and spins up as it unfolds or folds.
    this.cog.rotation.z += dt * (.12 + (1 - Math.min(1, this.presence)) * 2.2); this.band.visible = this.shadow.visible = presence > .01;
    (this.shadow.material as THREE.ShaderMaterial).uniforms.fade.value = Math.min(1, presence);
    this.shadow.scale.setScalar(.7 + presence * .3);
    for (const socket of this.sockets.values()) {
      socket.appear = Math.min(1, socket.appear + dt / APPEAR);
      const out = this.folding ? presence : easeOutBack(Math.max(0, socket.appear));
      if (socket.greyed !== !!socket.node.disabled) this.paint(socket);
      // A greyed entry stays flat in its socket when hovered.
      const target = socket.node.disabled ? 0 : socket.node.id === this.hovered ? 1 : this.open.includes(socket.node.id) ? .55 : 0;
      socket.lift += (target - socket.lift) * Math.min(1, dt * 14);
      socket.flash = Math.max(0, socket.flash - dt * 3);
      socket.group.position.set(socket.from.x + (socket.to.x - socket.from.x) * out, socket.from.y + (socket.to.y - socket.from.y) * out, BUTTON_Z + socket.lift * 12);
      socket.group.scale.setScalar(Math.max(.001, (.35 + .65 * Math.min(1, out)) * (1 + socket.lift * .12 + socket.flash * .18)));
      socket.group.visible = socket.appear > 0 || this.folding;
      // A refused power is greyed and unlit; an armed one glows from behind; a chosen one flashes bright.
      socket.material.color.setScalar((socket.face ? socket.node.disabled ? .85 : 1 : .11) + socket.flash * .9);
      const glow = socket.node.disabled ? 0 : socket.node.armed ? .9 + Math.sin(this.time * 6) * .1 : socket.flash * .8;
      if (socket.glow) (socket.glow.material as THREE.SpriteMaterial).opacity += (glow - (socket.glow.material as THREE.SpriteMaterial).opacity) * Math.min(1, dt * 12);
      for (let k = 0; k < socket.glints.length; k++) {
        const glint = socket.glints[k], a = this.time * 2.6 + k * Math.PI;
        glint.visible = !!socket.node.autocast;
        glint.position.set(Math.cos(a) * SOCKET * .9, Math.sin(a) * SOCKET * .9, 8);
      }
    }
    // Every socket's glints shine alike, so each of the two shared materials is set once.
    for (let k = 0; k < this.glintMaterials.length; k++) this.glintMaterials[k].opacity = .75 + Math.sin(this.time * 9 + k * 2) * .2;
    for (const arc of this.arcs) arc.scale.setScalar(Math.max(.001, presence));
    for (let i = this.pulses.length - 1; i >= 0; i--) {
      const pulse = this.pulses[i];
      pulse.age += dt;
      const t = pulse.age / PULSE;
      pulse.mesh.scale.setScalar(1 + t * 1.6); (pulse.mesh.material as THREE.MeshBasicMaterial).opacity = (1 - t) ** 2;
      if (t >= 1) { pulse.mesh.removeFromParent(); this.spareRings.push(pulse.mesh); this.pulses.splice(i, 1); }
    }
  }

  dispose() {
    this.disposed = true; this.warming = []; this.requested.clear();
    // The kit owns the glyph faces and the button parts; a menu given a shared kit leaves them to its owner.
    if (this.ownsKit) this.kit.dispose();
    const shared = [this.metal, this.dullBezel, this.kit.bezel, this.kit.iron, this.hardware, this.shadeMaterial, ...this.glintMaterials];
    for (const socket of this.sockets.values()) { socket.group.traverse(object => { if (object instanceof THREE.Sprite || object instanceof THREE.Mesh) { const material = object.material as THREE.Material; if (!shared.includes(material as never)) material.dispose(); } }); }
    for (const arc of this.arcs) arc.traverse(object => { if (object instanceof THREE.Mesh) object.geometry.dispose(); });
    for (const mesh of [...this.pulses.map(pulse => pulse.mesh), ...this.spareRings]) (mesh.material as THREE.Material).dispose();
    this.pulseGeometry.dispose();
    this.band.traverse(object => { if (object instanceof THREE.Mesh) object.geometry.dispose(); });
    this.shadow.geometry.dispose(); (this.shadow.material as THREE.Material).dispose();
    this.dullBezel.dispose(); this.hardware.dispose(); this.dark.dispose(); this.metal.dispose(); this.shadeMaterial?.dispose();
    for (const material of this.glintMaterials) material.dispose();
  }
}
